/**
 * Backup Monitor (DWA Internal → Web).
 *
 * One card per registered backup job (backup_jobs, migration 035), fed by the
 * run reports each job POSTs to /webhooks/backup-status. Health is computed
 * server-side (backup_monitor.assess) so the light here matches what Lupa
 * would say —
 *   green  = ok
 *   amber  = stale (no report within the job's window, or never)
 *   red    = failed run, or backup holds fewer objects than the source
 * Below the summary, the job's recent runs (newest first).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchBackupMonitor,
  type BackupHealth,
  type BackupJob,
  type BackupMonitorResponse,
  type BackupRun,
} from '../services/dashboardApi';
import type { ScorecardTheme } from './ScorecardView';

export interface BackupMonitorProps {
  theme: ScorecardTheme;
}

const AMBER = '#f59e0b';

const HEALTH_LABEL: Record<BackupHealth, string> = {
  ok: 'Healthy',
  stale: 'Overdue',
  incomplete: 'Incomplete',
  failed: 'Failed',
};

function healthColor(health: BackupHealth, theme: ScorecardTheme): string {
  if (health === 'ok') return theme.positive;
  if (health === 'stale') return AMBER;
  return theme.negative;
}

// ─── Formatting ───────────────────────────────────────────────────────────

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function ageDays(iso: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86400_000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function fmtDuration(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const secs = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (!Number.isFinite(secs) || secs < 0) return '—';
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function fmtCount(n: number | null): string {
  return n == null ? '—' : n.toLocaleString('en-US');
}

/** Decimal units, matching how Cloudflare and the handoff report bucket size. */
function fmtBytes(n: number | null): string {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) { v /= 1000; i += 1; }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

// ─── Pieces ───────────────────────────────────────────────────────────────

const Stat: React.FC<{ label: string; value: string; sub?: string; theme: ScorecardTheme; color?: string }> = ({ label, value, sub, theme, color }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: theme.textMuted }}>{label}</div>
    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: color ?? theme.textPrimary, marginTop: '0.15rem' }}>{value}</div>
    {sub && <div style={{ fontSize: '0.7rem', color: theme.textMuted, marginTop: '0.1rem' }}>{sub}</div>}
  </div>
);

const HistoryTable: React.FC<{ runs: BackupRun[]; theme: ScorecardTheme }> = ({ runs, theme }) => {
  const th: React.CSSProperties = {
    textAlign: 'left', fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: theme.textMuted, padding: '0.35rem 0.5rem',
    borderBottom: `1px solid ${theme.rowDivider}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = {
    fontSize: '0.78rem', color: theme.textPrimary, padding: '0.4rem 0.5rem',
    borderBottom: `1px solid ${theme.rowDivider}`, whiteSpace: 'nowrap',
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Finished</th>
            <th style={th}>Status</th>
            <th style={th}>Duration</th>
            <th style={{ ...th, textAlign: 'right' }}>Source objects</th>
            <th style={{ ...th, textAlign: 'right' }}>Backup objects</th>
            <th style={{ ...th, textAlign: 'right' }}>Backup size</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => {
            const behind = r.source_count != null && r.backup_count != null && r.backup_count < r.source_count;
            return (
              <tr key={r.run_id} title={r.run_id}>
                <td style={td}>{fmtDateTime(r.finished_at ?? r.received_at)}</td>
                <td style={{ ...td, color: r.status === 'success' ? theme.positive : theme.negative, fontWeight: 600 }}>
                  {r.status === 'success' ? 'Success' : `Failed${r.exit_code != null ? ` (${r.exit_code})` : ''}`}
                </td>
                <td style={td}>{fmtDuration(r.started_at, r.finished_at)}</td>
                <td style={{ ...td, textAlign: 'right' }}>{fmtCount(r.source_count)}</td>
                <td style={{ ...td, textAlign: 'right', color: behind ? theme.negative : theme.textPrimary, fontWeight: behind ? 600 : 400 }}>
                  {fmtCount(r.backup_count)}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>{fmtBytes(r.backup_bytes)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const JobCard: React.FC<{ job: BackupJob; theme: ScorecardTheme }> = ({ job, theme }) => {
  const color = healthColor(job.health, theme);
  const latest = job.latest;
  const alarming = job.health !== 'ok';
  return (
    <div style={{
      background: theme.cardBg,
      border: `1px solid ${alarming ? color : theme.cardBorder}`,
      boxShadow: theme.cardShadow, borderRadius: 4, padding: '1rem 1.15rem',
      display: 'flex', flexDirection: 'column', gap: '0.85rem',
    }}>
      {/* Header: client + job, health light */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: theme.textPrimary }}>{job.client_name}</div>
          <div style={{ fontSize: '0.74rem', color: theme.textMuted, marginTop: '0.15rem' }}>
            {job.job}{job.schedule && <> · {job.schedule}</>}
          </div>
          {job.description && (
            <div style={{ fontSize: '0.78rem', color: theme.textSecondary, marginTop: '0.35rem', lineHeight: 1.4 }}>{job.description}</div>
          )}
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0, fontSize: '0.75rem', fontWeight: 700, color }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'inline-block' }} />
          {HEALTH_LABEL[job.health]}
        </span>
      </div>

      {job.reasons.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.8rem', color: alarming ? color : theme.textSecondary, lineHeight: 1.5 }}>
          {job.reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}

      {latest ? (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.9rem',
          borderTop: `1px solid ${theme.rowDivider}`, paddingTop: '0.75rem',
        }}>
          <Stat theme={theme} label="Last report" value={ageDays(latest.received_at) || '—'} sub={fmtDateTime(latest.received_at)}
            color={job.health === 'stale' ? AMBER : undefined} />
          <Stat theme={theme} label="Last run" value={latest.status === 'success' ? 'Success' : 'Failed'}
            sub={`took ${fmtDuration(latest.started_at, latest.finished_at)}`}
            color={latest.status === 'success' ? theme.positive : theme.negative} />
          <Stat theme={theme} label="Source" value={fmtCount(latest.source_count)} sub={fmtBytes(latest.source_bytes)} />
          <Stat theme={theme} label="Backup" value={fmtCount(latest.backup_count)} sub={fmtBytes(latest.backup_bytes)}
            color={job.health === 'incomplete' ? theme.negative : undefined} />
        </div>
      ) : (
        <div style={{ fontSize: '0.82rem', color: theme.textMuted, borderTop: `1px solid ${theme.rowDivider}`, paddingTop: '0.75rem' }}>
          Awaiting the first report from this job.
        </div>
      )}

      {job.history.length > 0 && (
        <div>
          <div style={{
            fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase',
            color: theme.sectionTitle, marginBottom: '0.35rem',
          }}>Recent runs</div>
          <HistoryTable runs={job.history} theme={theme} />
        </div>
      )}
    </div>
  );
};

const Legend: React.FC<{ theme: ScorecardTheme }> = ({ theme }) => (
  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.72rem', color: theme.textSecondary }}>
    {([['Healthy', theme.positive], ['Overdue — no recent report', AMBER], ['Failed or incomplete', theme.negative]] as [string, string][]).map(([label, c]) => (
      <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'inline-block' }} />
        {label}
      </span>
    ))}
  </div>
);

// ─── Main view ────────────────────────────────────────────────────────────

export const BackupMonitorView: React.FC<BackupMonitorProps> = ({ theme }) => {
  const [data, setData] = useState<BackupMonitorResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchBackupMonitor());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = { ok: 0, attention: 0 };
    for (const j of data?.jobs ?? []) c[j.health === 'ok' ? 'ok' : 'attention'] += 1;
    return c;
  }, [data]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.textPrimary, margin: 0 }}>Backup Monitor</h2>
          <p style={{ fontSize: '0.8rem', color: theme.textMuted, marginTop: '0.25rem' }}>
            Automated client backups, reported by each job after every run.
            {data && <> · <span style={{ color: theme.positive }}>{counts.ok} healthy</span> · <span style={{ color: counts.attention ? theme.negative : theme.textMuted }}>{counts.attention} need attention</span></>}
            {data?.checked_at && <> · checked {new Date(data.checked_at).toLocaleString()}</>}
          </p>
        </div>
        <button
          type="button" onClick={load} disabled={loading}
          style={{
            fontSize: '0.78rem', fontWeight: 600, padding: '0.4rem 0.85rem',
            borderRadius: 4, cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit',
            background: 'transparent', color: theme.textSecondary,
            border: `1px solid ${theme.cardBorder}`, opacity: loading ? 0.6 : 1,
          }}
        >{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      <Legend theme={theme} />

      {error && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.negative}`,
          borderRadius: 4, padding: '1rem 1.25rem', color: theme.negative, fontSize: '0.85rem',
        }}>Failed to load Backup Monitor: {error}</div>
      )}

      {!error && data && data.job_count === 0 && !loading && (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: 4, padding: '2rem', textAlign: 'center', color: theme.textMuted,
        }}>No backup jobs registered yet.</div>
      )}

      {!error && data?.jobs.map((j) => <JobCard key={`${j.client}/${j.job}`} job={j} theme={theme} />)}
    </div>
  );
};

export default BackupMonitorView;
